import { type PhilatelicItem, type ReviewState, type ObjectId, type WithoutIdAndReview } from "@briefmarken_katalog/types"
import { GetPage, GetAllType, GetOne, GetOneType, PostPhilatelicItemSuggestion, PostPhilatelicItem, PostPhilatelicItemsBatch, DeleteAllPhilatelicItems, DeleteSuggestion, ReviewSuggestion, GetUploadToken, MoveItemToReview } from "$lib/services/requests"

export async function postItemSuggestion(item: WithoutIdAndReview<PhilatelicItem>, login:()=>Promise<string>): Promise<void>
{
  await new PostPhilatelicItemSuggestion(item, login).execute();
}

export async function postItem(item: PhilatelicItem, login:()=>Promise<string>): Promise<void>
{
  await new PostPhilatelicItem(item, login).execute();
}

const IMPORT_BATCH_SIZE = 500;

export async function setItems(
  items: PhilatelicItem[],
  doLogin: () => Promise<string>,
  onProgress?: (done: number, total: number) => void,
): Promise<boolean>
{
  await deleteAll(doLogin);
  onProgress?.(0, items.length);
  for (let i = 0; i < items.length; i += IMPORT_BATCH_SIZE) {
    const batch = items.slice(i, i + IMPORT_BATCH_SIZE);
    await new PostPhilatelicItemsBatch(batch, doLogin).execute();
    onProgress?.(Math.min(i + IMPORT_BATCH_SIZE, items.length), items.length);
  }
  return true;
}

async function fetchAllPages(type: GetAllType, login: () => Promise<string>): Promise<PhilatelicItem[] | null>
{
  const PAGE_SIZE = 500;
  const accumulated: PhilatelicItem[] = [];
  let page = 0;
  let total = Infinity;
  while (accumulated.length < total) {
    const result = await new GetPage(type, page, PAGE_SIZE, login).execute();
    if (!result) return null;
    total = result.total;
    accumulated.push(...result.items);
    page++;
  }
  return accumulated;
}

export async function getAllItems(login: () => Promise<string>): Promise<PhilatelicItem[] | null>
{
  return fetchAllPages(GetAllType.Items, login);
}

export async function getAllSuggestions(login: () => Promise<string>): Promise<PhilatelicItem[] | null>
{
  return fetchAllPages(GetAllType.Suggestions, login);
}

export async function getItem(id: string, login: () => Promise<string>): Promise<PhilatelicItem | null>
{
  return await new GetOne(GetOneType.Item, id, login).execute();
}

export async function getSuggestion(id: string, login: () => Promise<string>): Promise<PhilatelicItem | null>
{
  return await new GetOne(GetOneType.Suggestion, id, login).execute();
}

export async function reviewSuggestion(id: ObjectId, state: ReviewState, updates: Record<string, any> | undefined, login: () => Promise<string>): Promise<PhilatelicItem | null>
{
  return await new ReviewSuggestion(id, state, updates, login).execute();
}

export async function getUploadToken(login: () => Promise<string>): Promise<{ token: string }>
{
  return await new GetUploadToken(login).execute();
}

export async function deleteSuggestion(id: ObjectId, login: () => Promise<string>): Promise<void>
{
  await new DeleteSuggestion(id, login).execute();
}

export async function moveItemToReview(id: ObjectId, login: () => Promise<string>): Promise<void>
{
  await new MoveItemToReview(id, login).execute();
}

export async function deleteAll(login:()=>Promise<string>)
{
  await new DeleteAllPhilatelicItems(login).execute();
}
